import * as PIXI from "pixi.js";
import { GameStateManager } from "@/systems/GameStateManager";
import { SceneManager } from "@/systems/SceneManager";
import { EventSystem } from "@/systems/EventSystem";
import { MusicSystem } from "@/systems/MusicSystem";
import {
  GameScene,
  StateEffects,
  DayPhase,
  BeachPhase,
  WaveSize,
  WindCondition,
} from "@/types/GameState";

class SurfManagerGame {
  private app!: PIXI.Application;
  private gameStateManager: GameStateManager;
  private sceneManager!: SceneManager;
  private eventSystem: EventSystem;
  private musicSystem: MusicSystem;

  constructor() {
    this.gameStateManager = new GameStateManager();
    this.eventSystem = new EventSystem();
    this.musicSystem = new MusicSystem();
  }

  async init(): Promise<void> {
    // Create PIXI application with responsive sizing
    const width = Math.min(window.innerWidth, 800);
    const height = Math.min(window.innerHeight, 600);

    this.app = new PIXI.Application({
      width: width,
      height: height,
      backgroundColor: 0x1099bb,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    // Handle window resize
    window.addEventListener("resize", () => {
      const newWidth = Math.min(window.innerWidth, 800);
      const newHeight = Math.min(window.innerHeight, 600);
      this.app.renderer.resize(newWidth, newHeight);
    });

    // Add view to DOM
    const gameContainer = document.getElementById("game-container");
    if (gameContainer) {
      gameContainer.appendChild(this.app.view as HTMLCanvasElement);
    }

    // Initialize scene manager
    this.sceneManager = new SceneManager(this.app);

    // Add global interaction handler to ensure music starts on any user interaction
    // This helps with browser autoplay policies
    this.app.stage.eventMode = 'static';
    this.app.stage.on('pointerdown', () => {
      this.musicSystem.ensureMusicPlaying();
    });

    // Show loading screen
    await this.showLoadingScreen();

    // Load assets
    await this.loadAssets();

    // Create scenes
    this.createScenes();

    // Try to start music immediately (may be blocked by browser autoplay policy)
    this.musicSystem.startMainMusic();

    // Start with menu scene
    this.sceneManager.switchTo(GameScene.MENU);
  }

  private async showLoadingScreen(): Promise<void> {
    // Load the loading image first
    await PIXI.Assets.load("/assets/loading.jpg");

    // Create loading container
    const loadingContainer = new PIXI.Container();

    // Black background for padding
    const blackBg = new PIXI.Graphics();
    blackBg.beginFill(0x000000);
    blackBg.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    blackBg.endFill();
    loadingContainer.addChild(blackBg);

    // Loading image as background
    const loadingBg = PIXI.Sprite.from("/assets/loading.jpg");
    this.fitImageToScreen(loadingBg, this.app.screen.width, this.app.screen.height);
    loadingContainer.addChild(loadingBg);

    // "LOADING..." text at the top
    const loadingText = new PIXI.Text("LOADING...", {
      fontFamily: "Georgia",
      fontSize: 48,
      fontWeight: "bold",
      fill: 0xffffff,
      stroke: 0x000000,
      strokeThickness: 6,
      align: "center",
    });
    loadingText.anchor.set(0.5, 0);
    loadingText.x = this.app.screen.width / 2;
    loadingText.y = 40;
    loadingContainer.addChild(loadingText);

    // Add to stage
    this.app.stage.addChild(loadingContainer);

    // Store reference to remove later
    (this.app as any).loadingContainer = loadingContainer;
    // Store start time for minimum display duration
    (this.app as any).loadingStartTime = Date.now();

    // Allow a frame to render before continuing
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  private async loadAssets(): Promise<void> {
    // Load game assets
    await PIXI.Assets.load([
      "/assets/sprite_pack.png",
      "/assets/the_villa_background.png",
      "/assets/the_beach.png",
      "/assets/the_bar.png",
      "/assets/lagos.png",
      "/assets/cover.jpg",
      "/assets/success.jpg",
      "/assets/failure.jpg",
    ]);

    // Ensure loading screen is shown for at least 5 seconds
    const loadingStartTime = (this.app as any).loadingStartTime || Date.now();
    const elapsedTime = Date.now() - loadingStartTime;
    const minimumLoadingTime = 5000; // 5 seconds

    if (elapsedTime < minimumLoadingTime) {
      // Wait for remaining time
      await new Promise(resolve => setTimeout(resolve, minimumLoadingTime - elapsedTime));
    }

    // Remove loading screen
    const loadingContainer = (this.app as any).loadingContainer;
    if (loadingContainer) {
      this.app.stage.removeChild(loadingContainer);
    }
  }

  private createScenes(): void {
    // Create menu scene
    const menuScene = this.createMenuScene();
    this.sceneManager.registerScene(GameScene.MENU, menuScene);

    // Create instructions scene
    const instructionsScene = this.createInstructionsScene();
    this.sceneManager.registerScene(GameScene.INSTRUCTIONS, instructionsScene);

    // Create villa scene
    const villaScene = this.createVillaScene();
    this.sceneManager.registerScene(GameScene.VILLA, villaScene);

    // Create beach scene
    const beachScene = this.createBeachScene();
    this.sceneManager.registerScene(GameScene.BEACH, beachScene);

    // Create bar scene
    const barScene = this.createBarScene();
    this.sceneManager.registerScene(GameScene.BAR, barScene);
  }

  private fitImageToScreen(
    sprite: PIXI.Sprite,
    targetWidth: number,
    targetHeight: number
  ): void {
    // Calculate aspect ratios
    const imageAspect = sprite.texture.width / sprite.texture.height;
    const screenAspect = targetWidth / targetHeight;

    // Scale to fit while maintaining aspect ratio
    if (imageAspect > screenAspect) {
      // Image is wider than screen - fit to width
      sprite.width = targetWidth;
      sprite.height = targetWidth / imageAspect;
    } else {
      // Image is taller than screen - fit to height
      sprite.height = targetHeight;
      sprite.width = targetHeight * imageAspect;
    }

    // Center the image
    sprite.x = (targetWidth - sprite.width) / 2;
    sprite.y = (targetHeight - sprite.height) / 2;
  }

  private createMenuScene(): PIXI.Container {
    const scene = new PIXI.Container();

    // Black background for padding
    const blackBg = new PIXI.Graphics();
    blackBg.beginFill(0x000000);
    blackBg.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    blackBg.endFill();
    scene.addChild(blackBg);

    // Background - Cover image with aspect ratio preserved
    const bg = PIXI.Sprite.from("/assets/cover.jpg");
    this.fitImageToScreen(bg, this.app.screen.width, this.app.screen.height);
    scene.addChild(bg);

    // Store final position and scale for animation
    const finalX = bg.x;
    const finalY = bg.y;
    const finalScaleX = bg.scale.x;
    const finalScaleY = bg.scale.y;

    // Calculate zoom to show only 500x500px of the center
    // We need to zoom in so that 500px of the image fills the screen
    const imageWidth = bg.texture.width;
    const imageHeight = bg.texture.height;
    const zoomFactor = Math.max(
      imageWidth / 500,
      imageHeight / 500
    );

    // Set initial zoomed-in state
    bg.scale.x = finalScaleX * zoomFactor;
    bg.scale.y = finalScaleY * zoomFactor;

    // Center on the middle of the image
    bg.x = this.app.screen.width / 2 - (imageWidth / 2) * bg.scale.x;
    bg.y = this.app.screen.height / 2 - (imageHeight / 2) * bg.scale.y;

    // Animate to final position after 1 second
    const startTime = Date.now();
    const duration = 1000; // 1 second transition
    const delay = 1000; // 1 second delay before starting

    const animate = () => {
      const elapsed = Date.now() - startTime - delay;

      if (elapsed < 0) {
        // Still in delay period
        requestAnimationFrame(animate);
        return;
      }

      if (elapsed < duration) {
        // Ease in-out function (smooth curve)
        const t = elapsed / duration;
        const eased = t < 0.5
          ? 4 * t * t * t
          : 1 - Math.pow(-2 * t + 2, 3) / 2;

        // Interpolate scale
        bg.scale.x = finalScaleX * zoomFactor + (finalScaleX - finalScaleX * zoomFactor) * eased;
        bg.scale.y = finalScaleY * zoomFactor + (finalScaleY - finalScaleY * zoomFactor) * eased;

        // Interpolate position
        const startX = this.app.screen.width / 2 - (imageWidth / 2) * (finalScaleX * zoomFactor);
        const startY = this.app.screen.height / 2 - (imageHeight / 2) * (finalScaleY * zoomFactor);
        bg.x = startX + (finalX - startX) * eased;
        bg.y = startY + (finalY - startY) * eased;

        requestAnimationFrame(animate);
      } else {
        // Animation complete, set to final values
        bg.scale.x = finalScaleX;
        bg.scale.y = finalScaleY;
        bg.x = finalX;
        bg.y = finalY;
      }
    };

    // Start animation
    animate();

    // Start button - half size with transparency and blur
    const startButton = this.createMenuButton(
      "START GAME",
      "Begin your surf trip",
      this.app.screen.width / 2,
      340
    );
    startButton.on("pointerdown", () => {
      // Ensure music starts on first user interaction
      this.musicSystem.startMainMusic();
      this.startGame();
    });
    startButton.alpha = 0; // Start invisible
    scene.addChild(startButton);

    // Instructions button - half size with transparency and blur
    const instructionsButton = this.createMenuButton(
      "HOW TO PLAY",
      "Learn the basics",
      this.app.screen.width / 2,
      410
    );
    instructionsButton.on("pointerdown", () => {
      // Ensure music starts on first user interaction
      this.musicSystem.startMainMusic();
      this.sceneManager.switchTo(GameScene.INSTRUCTIONS);
    });
    instructionsButton.alpha = 0; // Start invisible
    scene.addChild(instructionsButton);

    // Fade in buttons after zoom animation completes (2 seconds delay + 1 second animation)
    const buttonFadeStart = Date.now();
    const buttonFadeDelay = 2000; // Start after zoom completes
    const buttonFadeDuration = 500; // 0.5 second fade

    const animateButtons = () => {
      const elapsed = Date.now() - buttonFadeStart - buttonFadeDelay;

      if (elapsed < 0) {
        // Still waiting for zoom to complete
        requestAnimationFrame(animateButtons);
        return;
      }

      if (elapsed < buttonFadeDuration) {
        // Fade in with ease-in-out
        const t = elapsed / buttonFadeDuration;
        const eased = t < 0.5
          ? 4 * t * t * t
          : 1 - Math.pow(-2 * t + 2, 3) / 2;

        startButton.alpha = eased;
        instructionsButton.alpha = eased;

        requestAnimationFrame(animateButtons);
      } else {
        // Fade complete
        startButton.alpha = 1;
        instructionsButton.alpha = 1;
      }
    };

    // Start button fade animation
    animateButtons();

    return scene;
  }

  private createInstructionsScene(): PIXI.Container {
    const scene = new PIXI.Container();

    // Dark background
    const bg = new PIXI.Graphics();
    bg.beginFill(0x1a1a2e);
    bg.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    bg.endFill();
    scene.addChild(bg);

    // Title
    const title = new PIXI.Text("HOW TO PLAY", {
      fontFamily: "Arial",
      fontSize: 36,
      fontWeight: "bold",
      fill: 0xffaa00,
      align: "center",
    });
    title.anchor.set(0.5);
    title.x = this.app.screen.width / 2;
    title.y = 40;
    scene.addChild(title);

    // Instructions text
    const instructions = [
      "OBJECTIVE:",
      "Survive 7 days managing amateur surfers in Sagres.",
      "Get 12+ surf sessions while keeping morale high!",
      "",
      "YOUR ROLE:",
      "You are Mike Duncan, the Surf Manager.",
      "Balance surfing, partying, and group happiness.",
      "",
      "KEY STATS:",
      "• Money - Budget for expenses (start: €800, need €200+)",
      "• Morale - Keep above 50 for victory (start: 70)",
      "• Energy - Needed for surfing (recovers +25/night)",
      "• Hangover - Avoid letting it exceed 80",
      "• Surfs - Your success metric (target: 12+)",
      "• Time - Activities cost hours. Midnight = day ends!",
      "• Lagos - Must visit at least once (mandatory!)",
      "",
      "CONTROLS:",
      "Click/tap buttons to navigate and make choices.",
      "Each decision affects your stats - choose wisely!",
      "",
      "TIPS:",
      "✓ Surf early in the trip while energy is high",
      "✓ Some partying is unavoidable (and expected!)",
      "✓ Watch your money - costs have increased!",
      "✓ Don't stay out past midnight - severe penalties!",
      "✓ Balance is key - victory is challenging!",
    ];

    const instructionsText = new PIXI.Text(instructions.join("\n"), {
      fontFamily: "Arial",
      fontSize: 13,
      fill: 0xffffff,
      align: "left",
      lineHeight: 18,
      wordWrap: true,
      wordWrapWidth: 700,
    });
    instructionsText.x = 50;
    instructionsText.y = 90;
    scene.addChild(instructionsText);

    // Back button
    const backButton = this.createButton(
      "BACK TO MENU",
      "Return to main menu",
      this.app.screen.width / 2,
      540
    );
    backButton.on("pointerdown", () => {
      // Ensure music starts on first user interaction
      this.musicSystem.startMainMusic();
      this.sceneManager.switchTo(GameScene.MENU);
    });
    scene.addChild(backButton);

    // View Manual button
    const manualButton = this.createSmallButton(
      "VIEW FULL MANUAL",
      this.app.screen.width - 120,
      560
    );
    manualButton.on("pointerdown", () => {
      window.open("/manual.html", "_blank");
    });
    scene.addChild(manualButton);

    return scene;
  }

  private createVillaScene(): PIXI.Container {
    const scene = new PIXI.Container();
    const state = this.gameStateManager.getState();

    // Background
    const bg = PIXI.Sprite.from("/assets/the_villa_background.png");
    bg.width = this.app.screen.width;
    bg.height = this.app.screen.height;
    scene.addChild(bg);

    // UI overlay
    const ui = this.createGameUI();
    scene.addChild(ui);

    // Location title with phase
    const titleSize = Math.min(28, this.app.screen.width / 20);
    let titleText = "THE VILLA";
    if (state.dayPhase === DayPhase.MORNING) {
      titleText = "THE VILLA - MORNING";
    }
    const locationTitle = new PIXI.Text(titleText, {
      fontFamily: "Georgia",
      fontSize: titleSize,
      fontWeight: "bold",
      fill: 0xffffff,
      stroke: 0x000000,
      strokeThickness: 4,
    });
    locationTitle.anchor.set(0.5);
    locationTitle.x = this.app.screen.width / 2;
    locationTitle.y = Math.max(80, this.app.screen.height * 0.15);
    scene.addChild(locationTitle);

    // Action buttons - stacked vertically
    const centerX = this.app.screen.width / 2;
    const startY = this.app.screen.height - 250;

    // Different buttons based on phase and whether went to Lagos
    if (state.wentToLagosToday) {
      // If went to Lagos, can only end the day
      const endDayButton = this.createButton(
        "END DAY",
        "The day is done after Lagos",
        centerX,
        startY + 90
      );
      endDayButton.on("pointerdown", () => this.endDay());
      scene.addChild(endDayButton);
    } else if (state.dayPhase === DayPhase.MORNING) {
      // Morning phase - can go to beach or rest
      const beachButton = this.createButton(
        "GO TO BEACH",
        "Head to the surf spot",
        centerX,
        startY
      );
      beachButton.on("pointerdown", () => {
        this.gameStateManager.setDayPhase(DayPhase.BEACH);
        this.gameStateManager.setBeachPhase(BeachPhase.CHECK_CONDITIONS);
        // Recreate beach scene with current state
        const beachScene = this.createBeachScene();
        this.sceneManager.registerScene(GameScene.BEACH, beachScene);
        this.sceneManager.switchTo(GameScene.BEACH);
      });
      scene.addChild(beachButton);

      const restButton = this.createButton(
        "REST AT VILLA",
        "Recover energy & reduce hangover",
        centerX,
        startY + 55
      );
      restButton.on("pointerdown", () => this.handleRestAtVilla());
      scene.addChild(restButton);

      const lagosButton = this.createButton(
        "GO TO LAGOS",
        "All-day party trip (no surfing!)",
        centerX,
        startY + 110
      );
      lagosButton.on("pointerdown", () => this.handleLagosTrip());
      scene.addChild(lagosButton);
    } else {
      // After beach, can go to bar or end day (but only if haven't been to bar yet today)
      if (!state.wentToBarToday) {
        // Show current time in button to make time consequences clear
        const timeStr = Math.floor(state.timeOfDay) + ":00";
        let barSubtitle = "Evening drinks with the lads";
        if (state.timeOfDay >= 20) {
          barSubtitle = `Already ${timeStr}! The lads are waiting...`;
        }

        const barButton = this.createButton(
          "GO TO BAR",
          barSubtitle,
          centerX,
          startY
        );
        barButton.on("pointerdown", () => {
          const state = this.gameStateManager.getState();
          this.gameStateManager.setDayPhase(DayPhase.BAR);
          this.gameStateManager.markBarVisited(); // Mark that we've visited the bar

          // Calculate morale penalty for arriving late
          let moralePenalty = 0;
          let penaltyMessage = "";

          if (state.timeOfDay >= 20 && state.timeOfDay < 22) {
            // Arriving at 8pm-10pm: severe penalty
            moralePenalty = -15;
            penaltyMessage =
              "Late arrival! The lads have been waiting to hit the bar and they're PISSED...";
          } else if (state.timeOfDay >= 22) {
            // Arriving at 10pm+: devastating penalty
            moralePenalty = -25;
            penaltyMessage =
              "Way too late! The lads are furious you surfed all day while they waited!";
          }

          // Advance time to 9pm if before that
          if (state.timeOfDay < 21) {
            this.gameStateManager.applyEffects({
              timeOfDay: 21 - state.timeOfDay,
            });
          }

          // Apply morale penalty if late
          if (moralePenalty < 0) {
            this.gameStateManager.applyEffects({ groupMorale: moralePenalty });
            this.showMessage(penaltyMessage);

            setTimeout(() => {
              // Recreate bar scene with current state
              const barScene = this.createBarScene();
              this.sceneManager.registerScene(GameScene.BAR, barScene);
              this.sceneManager.switchTo(GameScene.BAR);
            }, 2100);
          } else {
            // No penalty, go straight to bar
            const barScene = this.createBarScene();
            this.sceneManager.registerScene(GameScene.BAR, barScene);
            this.sceneManager.switchTo(GameScene.BAR);
          }
        });
        scene.addChild(barButton);
      }

      // Adjust button position based on whether bar button is shown
      const endDayY = state.wentToBarToday ? startY : startY + 55;
      const endDaySubtitle = state.wentToBarToday
        ? "Head to bed after drinks"
        : "Skip drinks and go to bed";

      const endDayButton = this.createButton(
        "END DAY",
        endDaySubtitle,
        centerX,
        endDayY
      );
      endDayButton.on("pointerdown", () => {
        const state = this.gameStateManager.getState();

        // If you surfed and it's still relatively early, skipping bar is okay
        // But if you surfed late and haven't been to the bar, the lads expect to go out
        if (
          state.surfedToday &&
          state.timeOfDay >= 18 &&
          !state.wentToBarToday
        ) {
          const moralePenalty = -18;
          this.gameStateManager.applyEffects({ groupMorale: moralePenalty });
          this.showMessage(
            "The lads wanted to celebrate the surf with drinks! They're absolutely gutted you skipped it..."
          );
          setTimeout(() => this.endDay(), 2100);
        } else {
          this.endDay();
        }
      });
      scene.addChild(endDayButton);
    }

    return scene;
  }

  private createBeachScene(): PIXI.Container {
    const scene = new PIXI.Container();
    const state = this.gameStateManager.getState();

    // Background
    const bg = PIXI.Sprite.from("/assets/the_beach.png");
    bg.width = this.app.screen.width;
    bg.height = this.app.screen.height;
    scene.addChild(bg);

    // UI overlay
    const ui = this.createGameUI();
    scene.addChild(ui);

    // Determine current beach phase (default to CHECK_CONDITIONS if null)
    const currentBeachPhase = state.beachPhase || BeachPhase.CHECK_CONDITIONS;

    // Location title with phase
    const titleSize = Math.min(28, this.app.screen.width / 20);
    let titleText = "THE BEACH";
    if (currentBeachPhase === BeachPhase.CHECK_CONDITIONS) {
      titleText = "THE BEACH - Checking Conditions";
    } else if (currentBeachPhase === BeachPhase.DECIDE_BEACH) {
      titleText = "THE BEACH - Pick Your Spot";
    } else if (currentBeachPhase === BeachPhase.SURF) {
      titleText = "THE BEACH - Session Time!";
    }
    const locationTitle = new PIXI.Text(titleText, {
      fontFamily: "Georgia",
      fontSize: titleSize,
      fontWeight: "bold",
      fill: 0xffffff,
      stroke: 0x000000,
      strokeThickness: 4,
    });
    locationTitle.anchor.set(0.5);
    locationTitle.x = this.app.screen.width / 2;
    locationTitle.y = Math.max(80, this.app.screen.height * 0.15);
    scene.addChild(locationTitle);

    // Action buttons based on beach phase
    const centerX = this.app.screen.width / 2;
    const startY = this.app.screen.height - 200;

    if (currentBeachPhase === BeachPhase.CHECK_CONDITIONS) {
      // Phase 1: Check conditions
      const checkButton = this.createButton(
        "CHECK CONDITIONS",
        "See what the waves are like",
        centerX,
        startY
      );
      checkButton.on("pointerdown", () => {
        // Checking conditions takes time (travel + assessment)
        this.gameStateManager.applyEffects({ timeOfDay: 1 });

        // Generate wave conditions
        const conditions = this.gameStateManager.generateWaveConditions();
        this.showWaveConditions(conditions, () => {
          // Always transition to DECIDE_BEACH phase first
          this.gameStateManager.setBeachPhase(BeachPhase.DECIDE_BEACH);

          // Random chance of event when checking conditions
          if (Math.random() < 0.2) {
            this.triggerRandomEvent();
          } else {
            this.refreshCurrentScene();
          }
        });
      });
      scene.addChild(checkButton);

      const backButton = this.createButton(
        "BACK TO VILLA",
        "Return without surfing",
        centerX,
        startY + 55
      );
      backButton.on("pointerdown", () => {
        this.gameStateManager.setBeachPhase(null);
        const villaScene = this.createVillaScene();
        this.sceneManager.registerScene(GameScene.VILLA, villaScene);
        this.sceneManager.switchTo(GameScene.VILLA);
      });
      scene.addChild(backButton);
    } else if (currentBeachPhase === BeachPhase.DECIDE_BEACH) {
      // Phase 2: Decide which beach
      const thisBeachButton = this.createButton(
        "SURF HERE",
        "Looks good enough",
        centerX,
        startY
      );
      thisBeachButton.on("pointerdown", () => {
        this.gameStateManager.setBeachPhase(BeachPhase.SURF);
        this.refreshCurrentScene();
      });
      scene.addChild(thisBeachButton);

      const otherBeachButton = this.createButton(
        "TRY ANOTHER SPOT",
        "Costs time & fuel",
        centerX,
        startY + 55
      );
      otherBeachButton.on("pointerdown", () => {
        const effects = { money: -20, timeOfDay: 1 };
        this.gameStateManager.applyEffects(effects);
        this.showResourceChanges(effects, "Drove to another beach...");

        // After driving, check conditions at the new spot
        setTimeout(() => {
          const conditions = this.gameStateManager.generateWaveConditions();
          this.showWaveConditions(conditions, () => {
            // Then go back to decide phase so they can choose again
            this.gameStateManager.setBeachPhase(BeachPhase.DECIDE_BEACH);
            this.refreshCurrentScene();
          });
        }, 2100);
      });
      scene.addChild(otherBeachButton);

      const backButton = this.createButton(
        "BACK TO VILLA",
        "Bail on the session",
        centerX,
        startY + 110
      );
      backButton.on("pointerdown", () => {
        this.gameStateManager.setBeachPhase(null);
        const villaScene = this.createVillaScene();
        this.sceneManager.registerScene(GameScene.VILLA, villaScene);
        this.sceneManager.switchTo(GameScene.VILLA);
      });
      scene.addChild(backButton);
    } else if (currentBeachPhase === BeachPhase.SURF) {
      // Phase 3: Surf
      const surfButton = this.createButton(
        "GO SURFING",
        "Get in the water!",
        centerX,
        startY
      );
      surfButton.on("pointerdown", () => this.handleSurfing());
      scene.addChild(surfButton);

      const backButton = this.createButton(
        "BACK TO VILLA",
        "Done for the day",
        centerX,
        startY + 55
      );
      backButton.on("pointerdown", () => {
        this.gameStateManager.setBeachPhase(null);
        const villaScene = this.createVillaScene();
        this.sceneManager.registerScene(GameScene.VILLA, villaScene);
        this.sceneManager.switchTo(GameScene.VILLA);
      });
      scene.addChild(backButton);
    }

    return scene;
  }

  private createBarScene(): PIXI.Container {
    const scene = new PIXI.Container();
    const state = this.gameStateManager.getState();

    // Background
    const bg = PIXI.Sprite.from("/assets/the_bar.png");
    bg.width = this.app.screen.width;
    bg.height = this.app.screen.height;
    scene.addChild(bg);

    // UI overlay
    const ui = this.createGameUI();
    scene.addChild(ui);

    // Location title with time
    const titleSize = Math.min(28, this.app.screen.width / 20);
    const hours = Math.floor(state.timeOfDay);
    // Display using 24-hour clock format (00:00-23:00, wraps after midnight)
    const displayHours = hours >= 24 ? hours - 24 : hours;
    const timeStr = `${displayHours.toString().padStart(2, "0")}:00`;
    const locationTitle = new PIXI.Text(`THE BAR - ${timeStr}`, {
      fontFamily: "Georgia",
      fontSize: titleSize,
      fontWeight: "bold",
      fill: 0xffffff,
      stroke: 0x000000,
      strokeThickness: 4,
    });
    locationTitle.anchor.set(0.5);
    locationTitle.x = this.app.screen.width / 2;
    locationTitle.y = Math.max(80, this.app.screen.height * 0.15);
    scene.addChild(locationTitle);

    // Warning if staying out late
    if (state.timeOfDay >= 26) {
      const warningSize = Math.min(16, this.app.screen.width / 40);
      const warning = new PIXI.Text(
        "It's almost 3am! Hangover will be brutal...",
        {
          fontFamily: "Georgia",
          fontSize: warningSize,
          fontStyle: "italic",
          fill: 0xff6666,
          stroke: 0x000000,
          strokeThickness: 2,
        }
      );
      warning.anchor.set(0.5);
      warning.x = this.app.screen.width / 2;
      warning.y = Math.max(120, this.app.screen.height * 0.22);
      scene.addChild(warning);
    } else if (state.timeOfDay >= 22) {
      const warningSize = Math.min(16, this.app.screen.width / 40);
      const warning = new PIXI.Text("Getting late! Hangover will be worse...", {
        fontFamily: "Georgia",
        fontSize: warningSize,
        fontStyle: "italic",
        fill: 0xff6666,
        stroke: 0x000000,
        strokeThickness: 2,
      });
      warning.anchor.set(0.5);
      warning.x = this.app.screen.width / 2;
      warning.y = Math.max(120, this.app.screen.height * 0.22);
      scene.addChild(warning);
    }

    // Action buttons - stacked vertically
    const centerX = this.app.screen.width / 2;
    const startY = this.app.screen.height - 200;

    // Can only have drinks if not too late (4am = 28:00) and have money
    // WARNING: Show that it's getting late
    const timeWarning =
      state.timeOfDay >= 26
        ? " [VERY LATE!]"
        : state.timeOfDay >= 23
        ? " [Getting late]"
        : "";

    if (state.timeOfDay < 28) {
      const drinkButton = this.createButton(
        "HAVE A DRINK" + timeWarning,
        "One more round (+1 hour)",
        centerX,
        startY
      );
      drinkButton.on("pointerdown", () => this.handleDrinking());
      scene.addChild(drinkButton);

      const partyButton = this.createButton(
        "FULL SESSION" + timeWarning,
        "Party hard (+3 hours)",
        centerX,
        startY + 55
      );
      partyButton.on("pointerdown", () => this.handlePartying());
      scene.addChild(partyButton);
    } else {
      // Past 4am - show only return option with warning
      const warningText = new PIXI.Text(
        "TOO LATE! It's 4am! Must return to villa now.\nYou'll pay for this tomorrow...",
        {
          fontFamily: "Georgia",
          fontSize: 18,
          fill: 0xff4444,
          align: "center",
          fontWeight: "bold",
          stroke: 0x000000,
          strokeThickness: 3,
        }
      );
      warningText.anchor.set(0.5);
      warningText.x = centerX;
      warningText.y = startY + 30;
      scene.addChild(warningText);
    }

    const backButton = this.createButton(
      "BACK TO VILLA",
      state.timeOfDay >= 28 ? "Time to sleep!" : "Call it a night",
      centerX,
      startY + 110
    );
    backButton.on("pointerdown", () => {
      // Recreate villa scene with current state before switching
      const villaScene = this.createVillaScene();
      this.sceneManager.registerScene(GameScene.VILLA, villaScene);
      this.sceneManager.switchTo(GameScene.VILLA);
    });
    scene.addChild(backButton);

    return scene;
  }

  private createLagosScene(): PIXI.Container {
    const scene = new PIXI.Container();

    // Background - Lagos image
    const bg = PIXI.Sprite.from("/assets/lagos.png");
    bg.width = this.app.screen.width;
    bg.height = this.app.screen.height;
    scene.addChild(bg);

    // Dark overlay for better text readability
    const overlay = new PIXI.Graphics();
    overlay.beginFill(0x000000, 0.6);
    overlay.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    overlay.endFill();
    scene.addChild(overlay);

    // Title
    const titleSize = Math.min(36, this.app.screen.width / 18);
    const title = new PIXI.Text("A NIGHT IN LAGOS", {
      fontFamily: "Georgia",
      fontSize: titleSize,
      fontWeight: "bold",
      fill: 0xffaa00,
      stroke: 0x000000,
      strokeThickness: 6,
      align: "center",
    });
    title.anchor.set(0.5);
    title.x = this.app.screen.width / 2;
    title.y = Math.max(60, this.app.screen.height * 0.12);
    scene.addChild(title);

    // Generate and display the story
    const story = this.generateLagosStory();
    const storySize = Math.min(18, this.app.screen.width / 32);
    const storyText = new PIXI.Text(story, {
      fontFamily: "Georgia",
      fontSize: storySize,
      fill: 0xffffff,
      stroke: 0x000000,
      strokeThickness: 3,
      align: "center",
      wordWrap: true,
      wordWrapWidth: Math.min(this.app.screen.width - 60, 650),
      lineHeight: storySize * 1.6,
    });
    storyText.anchor.set(0.5);
    storyText.x = this.app.screen.width / 2;
    storyText.y = this.app.screen.height / 2;
    scene.addChild(storyText);

    // Footer text
    const footerSize = Math.min(14, this.app.screen.width / 40);
    const footer = new PIXI.Text(
      "The lads crash in a Lagos hostel. Tomorrow will be rough...",
      {
        fontFamily: "Georgia",
        fontSize: footerSize,
        fontStyle: "italic",
        fill: 0xffdd88,
        stroke: 0x000000,
        strokeThickness: 2,
        align: "center",
      }
    );
    footer.anchor.set(0.5);
    footer.x = this.app.screen.width / 2;
    footer.y = this.app.screen.height - 80;
    scene.addChild(footer);

    // Continue button
    const continueButton = this.createButton(
      "NEXT MORNING",
      "Sleep it off...",
      this.app.screen.width / 2,
      this.app.screen.height - 40
    );
    continueButton.on("pointerdown", () => {
      // Lagos is an overnight trip - advance to next day
      this.endDay();
    });
    scene.addChild(continueButton);

    return scene;
  }

  private createGameUI(): PIXI.Container {
    const ui = new PIXI.Container();

    const state = this.gameStateManager.getState();

    // Stats panel with responsive sizing
    const fontSize = Math.min(16, this.app.screen.width / 40);
    const isMobile = this.app.screen.width < 500;

    let statsContent: string;
    if (isMobile) {
      // Vertical layout for mobile
      statsContent = `Day ${state.day} | €${state.money}\nMorale: ${state.groupMorale} | Energy: ${state.energy}\nHangover: ${state.hangoverLevel} | Surfs: ${state.surfsSoFar}`;
    } else {
      // Horizontal layout for desktop
      statsContent = `Day: ${state.day} | Money: €${state.money} | Morale: ${state.groupMorale}\nEnergy: ${state.energy} | Hangover: ${state.hangoverLevel} | Surfs: ${state.surfsSoFar}`;
    }

    // Stats background
    const statsBg = new PIXI.Graphics();
    statsBg.beginFill(0x000000, 0.6);
    statsBg.drawRoundedRect(
      5,
      5,
      this.app.screen.width - 10,
      isMobile ? 75 : 60,
      8
    );
    statsBg.endFill();
    ui.addChild(statsBg);

    const statsText = new PIXI.Text(statsContent, {
      fontFamily: "Georgia",
      fontSize: fontSize,
      fill: 0xffffff,
      lineHeight: fontSize * 1.3,
    });
    statsText.x = 10;
    statsText.y = 10;
    ui.addChild(statsText);

    // Time display in top right corner
    const timeSize = Math.min(20, this.app.screen.width / 35);
    const hours = Math.floor(state.timeOfDay);
    // Display time correctly using 24-hour clock (00:00-23:00)
    const displayHours = hours >= 24 ? hours - 24 : hours;
    const timeStr = `${displayHours.toString().padStart(2, "0")}:00`;

    const timeBg = new PIXI.Graphics();
    timeBg.beginFill(0x000000, 0.7);
    timeBg.drawRoundedRect(this.app.screen.width - 85, 5, 80, 35, 8);
    timeBg.endFill();
    ui.addChild(timeBg);

    const timeText = new PIXI.Text(timeStr, {
      fontFamily: "Georgia",
      fontSize: timeSize,
      fontWeight: "bold",
      fill: 0xffd700,
      align: "center",
    });
    timeText.anchor.set(0.5);
    timeText.x = this.app.screen.width - 45;
    timeText.y = 22;
    ui.addChild(timeText);

    return ui;
  }

  private createButton(
    text: string,
    subtitle: string,
    x: number,
    y: number
  ): PIXI.Container {
    const button = new PIXI.Container();
    button.x = x;
    button.y = y;
    button.eventMode = "static";
    button.cursor = "pointer";

    // Responsive button sizing - compact buttons
    const buttonWidth = Math.min(240, this.app.screen.width - 40);
    const buttonHeight = 48;
    const fontSize = Math.min(15, this.app.screen.width / 32);
    const subtitleSize = Math.min(11, this.app.screen.width / 50);

    const bg = new PIXI.Graphics();
    bg.beginFill(0x2d5f8d);
    bg.drawRoundedRect(
      -buttonWidth / 2,
      -buttonHeight / 2,
      buttonWidth,
      buttonHeight,
      6
    );
    bg.endFill();

    const label = new PIXI.Text(text, {
      fontFamily: "Georgia",
      fontSize: fontSize,
      fontWeight: "bold",
      fill: 0xffffff,
      wordWrap: true,
      wordWrapWidth: buttonWidth - 10,
      align: "center",
    });
    label.anchor.set(0.5);
    label.y = -8;

    const subtitleText = new PIXI.Text(subtitle, {
      fontFamily: "Georgia",
      fontSize: subtitleSize,
      fontStyle: "italic",
      fill: 0xccddff,
      wordWrap: true,
      wordWrapWidth: buttonWidth - 10,
      align: "center",
    });
    subtitleText.anchor.set(0.5);
    subtitleText.y = 8;

    button.addChild(bg, label, subtitleText);

    // Hover effect
    button.on("pointerover", () => {
      bg.clear();
      bg.beginFill(0x3a7ab5);
      bg.drawRoundedRect(
        -buttonWidth / 2,
        -buttonHeight / 2,
        buttonWidth,
        buttonHeight,
        6
      );
      bg.endFill();
    });
    button.on("pointerout", () => {
      bg.clear();
      bg.beginFill(0x2d5f8d);
      bg.drawRoundedRect(
        -buttonWidth / 2,
        -buttonHeight / 2,
        buttonWidth,
        buttonHeight,
        6
      );
      bg.endFill();
    });

    return button;
  }

  private createMenuButton(
    text: string,
    subtitle: string,
    x: number,
    y: number
  ): PIXI.Container {
    const button = new PIXI.Container();
    button.x = x;
    button.y = y;
    button.eventMode = "static";
    button.cursor = "pointer";

    // Half size compared to regular buttons
    const buttonWidth = Math.min(120, (this.app.screen.width - 40) / 2);
    const buttonHeight = 24;
    const fontSize = Math.min(8, this.app.screen.width / 64);
    const subtitleSize = Math.min(6, this.app.screen.width / 100);

    // Semi-transparent background with blur effect
    const bg = new PIXI.Graphics();
    bg.beginFill(0x2d5f8d, 0.5); // 50% opacity
    bg.drawRoundedRect(
      -buttonWidth / 2,
      -buttonHeight / 2,
      buttonWidth,
      buttonHeight,
      6
    );
    bg.endFill();

    // Add blur filter
    const blurFilter = new PIXI.BlurFilter();
    blurFilter.blur = 2;
    bg.filters = [blurFilter];

    const label = new PIXI.Text(text, {
      fontFamily: "Georgia",
      fontSize: fontSize,
      fontWeight: "bold",
      fill: 0xffffff,
      wordWrap: true,
      wordWrapWidth: buttonWidth - 10,
      align: "center",
    });
    label.anchor.set(0.5);
    label.y = -4;

    const subtitleText = new PIXI.Text(subtitle, {
      fontFamily: "Georgia",
      fontSize: subtitleSize,
      fontStyle: "italic",
      fill: 0xccddff,
      wordWrap: true,
      wordWrapWidth: buttonWidth - 10,
      align: "center",
    });
    subtitleText.anchor.set(0.5);
    subtitleText.y = 4;

    button.addChild(bg, label, subtitleText);

    // Hover effect
    button.on("pointerover", () => {
      bg.clear();
      bg.beginFill(0x3a7ab5, 0.7); // More opaque on hover
      bg.drawRoundedRect(
        -buttonWidth / 2,
        -buttonHeight / 2,
        buttonWidth,
        buttonHeight,
        6
      );
      bg.endFill();
    });
    button.on("pointerout", () => {
      bg.clear();
      bg.beginFill(0x2d5f8d, 0.5);
      bg.drawRoundedRect(
        -buttonWidth / 2,
        -buttonHeight / 2,
        buttonWidth,
        buttonHeight,
        6
      );
      bg.endFill();
    });

    return button;
  }

  private createSmallButton(
    text: string,
    x: number,
    y: number
  ): PIXI.Container {
    const button = new PIXI.Container();
    button.x = x;
    button.y = y;
    button.eventMode = "static";
    button.cursor = "pointer";

    const bg = new PIXI.Graphics();
    bg.beginFill(0x555555);
    bg.drawRoundedRect(-80, -18, 160, 36, 8);
    bg.endFill();

    const label = new PIXI.Text(text, {
      fontFamily: "Arial",
      fontSize: 14,
      fill: 0xffffff,
    });
    label.anchor.set(0.5);

    button.addChild(bg, label);

    // Hover effect
    button.on("pointerover", () => {
      bg.clear();
      bg.beginFill(0x666666);
      bg.drawRoundedRect(-80, -18, 160, 36, 8);
      bg.endFill();
    });
    button.on("pointerout", () => {
      bg.clear();
      bg.beginFill(0x555555);
      bg.drawRoundedRect(-80, -18, 160, 36, 8);
      bg.endFill();
    });

    return button;
  }

  private startGame(): void {
    this.gameStateManager.reset();
    this.sceneManager.switchTo(GameScene.VILLA);
  }

  private checkImmediateFailure(): boolean {
    const failureMessage = this.gameStateManager.checkFailureConditions();
    if (failureMessage) {
      this.showMessage(failureMessage);
      setTimeout(() => this.sceneManager.switchTo(GameScene.MENU), 4000);
      return true;
    }
    return false;
  }

  private handleSurfing(): void {
    const state = this.gameStateManager.getState();

    if (state.energy < 20) {
      this.showMessage("Too tired to surf! Rest up first.");
      return;
    }

    const conditions = this.gameStateManager.getCurrentConditions();
    let surfsSoFar = 0;
    let energyCost = -20;
    let moraleBump = 10;
    let message = "Great surf session! The lads are stoked!";

    if (conditions) {
      // Adjust based on wave size
      if (conditions.size === WaveSize.FLAT) {
        surfsSoFar = 0;
        moraleBump = -10;
        message = "Totally flat! No waves caught. The lads are bummed.";
      } else if (conditions.size === WaveSize.SMALL) {
        surfsSoFar = 1;
        moraleBump = -5;
        message = "Small waves but frustrating. The lads wanted more!";
      } else if (conditions.size === WaveSize.MEDIUM) {
        surfsSoFar = 1;
        moraleBump = -2;
        message = "Average session. Nothing special, lads are a bit underwhelmed.";
      } else if (conditions.size === WaveSize.GOOD) {
        surfsSoFar = 2; // Good conditions = bonus waves
        moraleBump = 20;
        energyCost = -25;
        message = "Epic session! Perfect conditions, multiple waves!";
      } else if (conditions.size === WaveSize.BIG) {
        // Big waves - scary for some lads
        if (conditions.gnarliness >= 8) {
          // Some lads refuse to go out
          if (Math.random() < 0.4) {
            surfsSoFar = 1;
            moraleBump = -12;
            energyCost = -30;
            message = "Too gnarly! Half the lads stayed on the beach, feeling inadequate.";
          } else {
            surfsSoFar = 2;
            moraleBump = 8;
            energyCost = -35;
            message = "Heavy session! Only the brave got waves. Others are jealous.";
          }
        } else {
          surfsSoFar = 2;
          moraleBump = 12;
          energyCost = -30;
          message = "Big waves! Challenging and exhausting. Mixed feelings.";
        }
      } else if (conditions.size === WaveSize.HUGE) {
        // Huge waves - most lads are scared
        if (Math.random() < 0.6) {
          surfsSoFar = 0;
          moraleBump = -20;
          energyCost = -10;
          message = "TOO BIG! Everyone chickened out. Feeling defeated.";
        } else {
          surfsSoFar = 1;
          moraleBump = 0;
          energyCost = -40;
          message = "Absolutely terrifying! One brave soul got a wave, others feel emasculated.";
        }
      }

      // Adjust based on wind (affects morale)
      if (conditions.wind === WindCondition.OFFSHORE) {
        moraleBump += 5; // Clean conditions boost morale
      } else if (conditions.wind === WindCondition.ONSHORE) {
        moraleBump -= 3; // Messy conditions reduce morale
      }
    } else {
      // No conditions set - default behavior
      surfsSoFar = 1;
    }

    const effects = {
      surfsSoFar,
      energy: energyCost,
      groupMorale: moraleBump,
      timeOfDay: 4, // Surfing takes 4 hours (increased from 2 to make time a real constraint)
    };

    this.gameStateManager.applyEffects(effects);
    this.gameStateManager.markSurfed();
    this.showResourceChanges(effects, message);

    // Refresh scene after the overlay is gone
    setTimeout(() => {
      if (this.checkImmediateFailure()) return;

      this.refreshCurrentScene();

      // Random chance of triggering event
      if (Math.random() < 0.3) {
        setTimeout(() => this.triggerRandomEvent(), 500);
      }
    }, 2100);
  }

  private handleDrinking(): void {
    const state = this.gameStateManager.getState();

    const drinkCost = 40; // Increased from 30 to 40

    if (state.money < drinkCost) {
      this.showMessage("Not enough money for drinks!");
      return;
    }

    // Hangover increases more if staying out late - SIGNIFICANTLY increased penalty
    const lateNightPenalty = state.timeOfDay >= 22 ? 15 : 0;

    const effects = {
      money: -drinkCost,
      hangoverLevel: 12 + lateNightPenalty,
      groupMorale: 8,
      energy: -10,
      timeOfDay: 1, // One hour passes
    };

    this.gameStateManager.applyEffects(effects);
    this.showResourceChanges(effects, "One more round with the boys...");

    setTimeout(() => {
      if (this.checkImmediateFailure()) return;
      this.refreshCurrentScene();

      // Chance of triggering bar event after drinking
      if (Math.random() < 0.3) {
        setTimeout(() => this.triggerRandomEvent(), 500);
      }
    }, 2100);
  }

  private handlePartying(): void {
    const state = this.gameStateManager.getState();

    const partyCost = 80; // Increased from 60 to 80

    if (state.money < partyCost) {
      this.showMessage("Not enough money for a full party!");
      return;
    }

    // Hangover increases more if staying out late - SIGNIFICANTLY increased penalty
    const lateNightPenalty = state.timeOfDay >= 22 ? 20 : 0;

    const effects = {
      money: -partyCost,
      hangoverLevel: 25 + lateNightPenalty,
      groupMorale: 20,
      energy: -30,
      partyCount: 1,
      timeOfDay: 3, // Three hours pass
    };

    this.gameStateManager.applyEffects(effects);
    this.showResourceChanges(
      effects,
      "Epic session! Tomorrow might be rough..."
    );

    setTimeout(() => {
      if (this.checkImmediateFailure()) return;

      this.refreshCurrentScene();

      // High chance of triggering event after partying
      if (Math.random() < 0.5) {
        setTimeout(() => this.triggerRandomEvent(), 500);
      }
    }, 2100);
  }

  private handleLagosTrip(): void {
    const state = this.gameStateManager.getState();

    const lagosCost = 150; // Increased from 120 to 150

    if (state.money < lagosCost) {
      this.showMessage("Not enough money for Lagos!");
      return;
    }

    // Apply effects immediately
    const effects = {
      money: -lagosCost,
      hangoverLevel: 50,
      groupMorale: 35,
      energy: -60,
      partyCount: 1,
    };

    this.gameStateManager.applyEffects(effects);
    this.gameStateManager.goToLagos();

    // Switch to Lagos music
    this.musicSystem.playLagosMusic();

    // Show Lagos scene with story
    const lagosScene = this.createLagosScene();
    this.sceneManager.registerScene(GameScene.VILLA, lagosScene); // Temporarily register as VILLA
    this.sceneManager.switchTo(GameScene.VILLA);
  }

  private handleRestAtVilla(): void {
    const effects = {
      energy: 20,
      hangoverLevel: -15,
      timeOfDay: 2, // Resting takes 2 hours (new - makes time management meaningful)
    };

    this.gameStateManager.applyEffects(effects);
    this.showResourceChanges(
      effects,
      "You rest at the villa and recover some energy."
    );

    setTimeout(() => {
      if (this.checkImmediateFailure()) return;
      this.refreshCurrentScene();
    }, 2100);
  }

  private endDay(): void {
    const state = this.gameStateManager.getState();

    // Check if this will be day 7 (end of trip)
    if (state.day >= 7) {
      this.showDayWrapUp(); // Show final day wrap-up first
      return;
    }

    // Show wrap-up screen before advancing
    this.showDayWrapUp();
  }

  private showDayWrapUp(): void {
    const state = this.gameStateManager.getState();

    // Create overlay
    const overlay = new PIXI.Container();

    // Semi-transparent background
    const bg = new PIXI.Graphics();
    bg.beginFill(0x000000, 0.9);
    bg.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    bg.endFill();
    overlay.addChild(bg);

    // Panel
    const panelWidth = Math.min(this.app.screen.width - 40, 500);
    const panelHeight = Math.min(this.app.screen.height - 100, 450);
    const panelX = (this.app.screen.width - panelWidth) / 2;
    const panelY = (this.app.screen.height - panelHeight) / 2;

    const panel = new PIXI.Graphics();
    panel.beginFill(0x1a2a3a);
    panel.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, 15);
    panel.endFill();
    overlay.addChild(panel);

    // Title
    const titleSize = Math.min(28, this.app.screen.width / 22);
    const title = new PIXI.Text(`DAY ${state.day} WRAP-UP`, {
      fontFamily: "Georgia",
      fontSize: titleSize,
      fontWeight: "bold",
      fill: 0xffd700,
      align: "center",
    });
    title.anchor.set(0.5, 0);
    title.x = this.app.screen.width / 2;
    title.y = panelY + 20;
    overlay.addChild(title);

    // Generate humorous commentary
    const commentary = this.generateDayCommentary(state);

    const commentSize = Math.min(16, this.app.screen.width / 35);

    // Calculate maximum height available for commentary (leave space for stats and button)
    const maxCommentaryHeight = panelHeight - 200; // Reserve 200px for title, stats, and button

    const commentText = new PIXI.Text(commentary, {
      fontFamily: "Georgia",
      fontSize: commentSize,
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: panelWidth - 60,
      lineHeight: commentSize * 1.5,
    });
    commentText.anchor.set(0.5, 0);
    commentText.x = this.app.screen.width / 2;
    commentText.y = panelY + 70;

    // If commentary is too tall, scale it down slightly
    if (commentText.height > maxCommentaryHeight) {
      const scaleFactor = maxCommentaryHeight / commentText.height;
      commentText.scale.set(scaleFactor);
    }

    overlay.addChild(commentText);

    // Stats summary - position based on commentary height
    const statsY = Math.max(
      panelY + 70 + commentText.height + 20,
      panelY + panelHeight - 140
    );
    const statsSize = Math.min(14, this.app.screen.width / 40);

    const surfText = state.surfedToday ? "✓ Surfed today" : "✗ No surfing";
    const lagosText = state.wentToLagosToday ? "🚗 Lagos trip!" : "";
    const drinkText =
      state.timeOfDay >= 23
        ? "🍺 Late night"
        : state.dayPhase === DayPhase.BAR
        ? "🍺 Hit the bar"
        : "😴 Early night";

    const statsContent = `${surfText}  ${drinkText}  ${lagosText}\n\nMorale: ${state.groupMorale} | Energy: ${state.energy} | Hangover: ${state.hangoverLevel}`;

    const statsText = new PIXI.Text(statsContent, {
      fontFamily: "Georgia",
      fontSize: statsSize,
      fill: 0xaaccff,
      align: "center",
      lineHeight: statsSize * 1.4,
    });
    statsText.anchor.set(0.5, 0);
    statsText.x = this.app.screen.width / 2;
    statsText.y = statsY;
    overlay.addChild(statsText);

    // Continue button
    const buttonY = panelY + panelHeight - 50;
    const continueButton = this.createButton(
      state.day >= 7 ? "SEE RESULTS" : "CONTINUE",
      "Click to proceed",
      this.app.screen.width / 2,
      buttonY
    );
    continueButton.on("pointerdown", () => {
      this.app.stage.removeChild(overlay);

      // Check if this was day 7 before advancing
      const wasDay7 = state.day >= 7;

      if (!wasDay7) {
        // Check if we went to Lagos today BEFORE calling nextDay (which resets this flag)
        const wentToLagosToday = state.wentToLagosToday;

        this.gameStateManager.nextDay();

        // Switch back to main music only after a Lagos day
        if (wentToLagosToday) {
          this.musicSystem.returnToMainMusic();
        }

        // Check for immediate failure conditions
        const failureMessage = this.gameStateManager.checkFailureConditions();
        if (failureMessage) {
          this.showMessage(failureMessage);
          setTimeout(() => this.sceneManager.switchTo(GameScene.MENU), 4000);
          return;
        }

        // Check if breakfast event should trigger (every 3-4 days)
        const newState = this.gameStateManager.getState();
        const daysSinceBreakfast = newState.day - newState.lastBreakfastDay;
        if (daysSinceBreakfast >= 3 && Math.random() < 0.6) {
          // 60% chance to trigger if it's been 3+ days
          const breakfastEvent = this.eventSystem.getEventById('breakfast_event');
          if (breakfastEvent) {
            // Update last breakfast day
            this.gameStateManager.setLastBreakfastDay(newState.day);
            this.showMessage(`Day ${newState.day} begins!`);
            setTimeout(() => {
              this.refreshCurrentScene();
              setTimeout(() => this.showEventDialog(breakfastEvent), 500);
            }, 2100);
            return;
          }
        }

        this.showMessage(`Day ${newState.day} begins!`);
        setTimeout(() => {
          this.refreshCurrentScene();
        }, 2100);
      } else {
        // Trip is complete! Show final results
        this.showGameOver();
      }
    });
    overlay.addChild(continueButton);

    this.app.stage.addChild(overlay);
  }

  private generateDayCommentary(state: any): string {
    const comments = [];

    // Surfing commentary
    if (state.wentToLagosToday) {
      comments.push(
        "The lads went to LAGOS! Mike Duncan couldn't stop them. Absolute carnage."
      );
    } else if (state.surfedToday) {
      if (state.surfsSoFar >= 10) {
        comments.push(
          "Another epic surf session! Mike Duncan is living the dream."
        );
      } else {
        comments.push("Got some waves today. The lads are moderately stoked.");
      }
    } else {
      comments.push("No surfing today? Mike Duncan is losing his touch...");
    }

    // Hangover commentary
    if (state.hangoverLevel >= 60) {
      comments.push("\n\nThe lads are SUFFERING. Tomorrow will be rough.");
    } else if (state.hangoverLevel >= 30) {
      comments.push(
        "\n\nA bit dusty, but nothing a greasy breakfast can't fix."
      );
    } else if (state.hangoverLevel > 0) {
      comments.push("\n\nBarely even hungover. Rookies.");
    }

    // Morale commentary
    if (state.groupMorale >= 80) {
      comments.push("\n\nThe group is BUZZING! Life is good in Sagres.");
    } else if (state.groupMorale <= 30) {
      comments.push(
        "\n\nMorale is in the gutter. Mike needs to work his magic."
      );
    }

    // Time commentary
    if (state.timeOfDay >= 24) {
      comments.push(
        "\n\nStayed out until closing time. Worth it? Probably not."
      );
    } else if (state.timeOfDay >= 23) {
      comments.push(
        "\n\nA late one, but at least they're in bed before midnight."
      );
    }

    // Money commentary
    if (state.money <= 100) {
      comments.push("\n\n💰 Running LOW on cash! Time to budget.");
    }

    return comments.join("");
  }

  private generateLagosStory(): string {
    // Array of humorous Lagos night scenarios
    const stories = [
      "Dave has chundered all over the hostel bathroom. The staff are NOT impressed. Withers is chirpsing the receptionist to smooth things over while Conroy wingmans like a champion. Meanwhile, Joey mentioned the J word at the club and scared off every bird in sight.",

      "Conroy is once again proving why he's the ultimate wingman, helping one of the lads chat up a group at the bar. Things are going well until Joey drops the J word and everyone scatters. Dave can be heard retching in the corner while Withers somehow pulls despite the chaos.",

      "The night started strong but Dave has absolutely lost it - there's sick everywhere in the hostel hallway. Withers is too busy chirpsing to notice. Joey ruined his chances early by mentioning the J word, but Conroy stepped in as wingman and saved the night for everyone else.",

      "What a disaster! Dave chundered on the dancefloor - legendary but messy. Joey scared off a whole group of birds by bringing up the J word within 5 minutes of meeting them. On the bright side, Withers is absolutely killing it with the chirpsing, and Conroy is wingmanning at an elite level.",

      "The hostel is going to ban the lads after tonight. Dave's sick is EVERYWHERE. Joey managed to mention the J word not once, but TWICE, sending birds running both times. Conroy is working overtime as wingman while Withers chirpses his way through half of Lagos.",

      "Classic Lagos chaos. Dave barely made it back to the hostel before chundering spectacularly. Withers spent the entire night chirpsing and somehow succeeded. Joey mentioned the J word early on and killed the vibe, but Conroy swooped in with his wingman skills to salvage the situation.",

      "The night was going perfectly until Dave decorated the hostel bathroom with his dinner. Joey mentioned the J word to a group of birds who immediately left. Conroy is living up to his wingman reputation though, and Withers is out there chirpsing like his life depends on it."
    ];

    // Return a random story
    return stories[Math.floor(Math.random() * stories.length)];
  }

  private triggerRandomEvent(): void {
    const state = this.gameStateManager.getState();
    const event = this.eventSystem.getRandomEventForPhase(
      state.dayPhase,
      state.eventsTriggeredToday
    );

    // Only show event if one is available for this phase
    if (event) {
      this.showEventDialog(event);
    }
  }

  private showEventDialog(event: any): void {
    // Mark this event as triggered for today
    this.gameStateManager.markEventTriggered(event.id);

    // Create event overlay
    const overlay = new PIXI.Container();

    // Semi-transparent background
    const bg = new PIXI.Graphics();
    bg.beginFill(0x000000, 0.8);
    bg.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    bg.endFill();
    overlay.addChild(bg);

    // Event panel
    const panel = new PIXI.Graphics();
    const panelWidth = Math.min(this.app.screen.width - 40, 600);
    const panelHeight = Math.min(this.app.screen.height - 100, 450);
    const panelX = (this.app.screen.width - panelWidth) / 2;
    const panelY = (this.app.screen.height - panelHeight) / 2;

    panel.beginFill(0x2a2a3e);
    panel.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, 15);
    panel.endFill();
    overlay.addChild(panel);

    // Event title
    const titleSize = Math.min(28, this.app.screen.width / 25);
    const title = new PIXI.Text(event.title, {
      fontFamily: "Arial",
      fontSize: titleSize,
      fontWeight: "bold",
      fill: 0xffaa00,
      align: "center",
      wordWrap: true,
      wordWrapWidth: panelWidth - 40,
    });
    title.anchor.set(0.5, 0);
    title.x = this.app.screen.width / 2;
    title.y = panelY + 20;
    overlay.addChild(title);

    // Event description
    const descSize = Math.min(16, this.app.screen.width / 40);
    const description = new PIXI.Text(event.description, {
      fontFamily: "Arial",
      fontSize: descSize,
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: panelWidth - 40,
      lineHeight: descSize * 1.4,
    });
    description.anchor.set(0.5, 0);
    description.x = this.app.screen.width / 2;
    description.y = panelY + 70;
    overlay.addChild(description);

    // Choice buttons
    const choiceStartY = panelY + 170;
    const choiceSpacing = 60;

    event.choices.forEach((choice: any, index: number) => {
      const choiceButton = this.createChoiceButton(
        choice.text,
        this.app.screen.width / 2,
        choiceStartY + index * choiceSpacing,
        panelWidth - 60
      );

      choiceButton.on("pointerdown", () => {
        this.gameStateManager.applyEffects(choice.effects);
        this.app.stage.removeChild(overlay);
        this.showResourceChanges(choice.effects, "Decision made!");

        setTimeout(() => {
          if (this.checkImmediateFailure()) return;
          this.refreshCurrentScene();
        }, 2100);
      });

      overlay.addChild(choiceButton);
    });

    this.app.stage.addChild(overlay);
  }

  private createChoiceButton(
    text: string,
    x: number,
    y: number,
    width: number
  ): PIXI.Container {
    const button = new PIXI.Container();
    button.x = x;
    button.y = y;
    button.eventMode = "static";
    button.cursor = "pointer";

    const buttonHeight = 50;
    const bg = new PIXI.Graphics();
    bg.beginFill(0x4a90e2);
    bg.drawRoundedRect(-width / 2, -buttonHeight / 2, width, buttonHeight, 8);
    bg.endFill();

    const fontSize = Math.min(14, width / 30);
    const label = new PIXI.Text(text, {
      fontFamily: "Arial",
      fontSize: fontSize,
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: width - 20,
    });
    label.anchor.set(0.5);

    button.addChild(bg, label);

    // Hover effect
    button.on("pointerover", () => {
      bg.clear();
      bg.beginFill(0x5aa0f2);
      bg.drawRoundedRect(-width / 2, -buttonHeight / 2, width, buttonHeight, 8);
      bg.endFill();
    });
    button.on("pointerout", () => {
      bg.clear();
      bg.beginFill(0x4a90e2);
      bg.drawRoundedRect(-width / 2, -buttonHeight / 2, width, buttonHeight, 8);
      bg.endFill();
    });

    return button;
  }

  private showMessage(text: string): void {
    // Create temporary message overlay
    const message = new PIXI.Container();

    const bg = new PIXI.Graphics();
    bg.beginFill(0x000000, 0.7);
    const msgWidth = Math.min(this.app.screen.width - 40, 500);
    const msgHeight = 80;
    bg.drawRoundedRect(
      (this.app.screen.width - msgWidth) / 2,
      this.app.screen.height - 150,
      msgWidth,
      msgHeight,
      10
    );
    bg.endFill();
    message.addChild(bg);

    const fontSize = Math.min(16, this.app.screen.width / 35);
    const label = new PIXI.Text(text, {
      fontFamily: "Arial",
      fontSize: fontSize,
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: msgWidth - 20,
    });
    label.anchor.set(0.5);
    label.x = this.app.screen.width / 2;
    label.y = this.app.screen.height - 110;
    message.addChild(label);

    this.app.stage.addChild(message);

    // Remove after 2 seconds
    setTimeout(() => {
      this.app.stage.removeChild(message);
    }, 2000);
  }

  private showWaveConditions(conditions: any, onClose?: () => void): void {
    // Create overlay
    const overlay = new PIXI.Container();

    // Semi-transparent background (interactive for closing)
    const bg = new PIXI.Graphics();
    bg.beginFill(0x000000, 0.9);
    bg.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    bg.endFill();
    bg.eventMode = 'static';
    bg.cursor = 'pointer';
    overlay.addChild(bg);

    // Panel
    const panelWidth = Math.min(this.app.screen.width - 40, 450);
    const panelHeight = Math.min(this.app.screen.height - 100, 400);
    const panelX = (this.app.screen.width - panelWidth) / 2;
    const panelY = (this.app.screen.height - panelHeight) / 2;

    const panel = new PIXI.Graphics();
    panel.beginFill(0x1a3a52);
    panel.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, 15);
    panel.endFill();
    overlay.addChild(panel);

    // Title
    const titleSize = Math.min(26, this.app.screen.width / 22);
    const title = new PIXI.Text("WAVE CONDITIONS", {
      fontFamily: "Georgia",
      fontSize: titleSize,
      fontWeight: "bold",
      fill: 0xffd700,
      align: "center",
    });
    title.anchor.set(0.5, 0);
    title.x = this.app.screen.width / 2;
    title.y = panelY + 20;
    overlay.addChild(title);

    // Description
    const descSize = Math.min(20, this.app.screen.width / 30);
    const description = new PIXI.Text(conditions.description, {
      fontFamily: "Georgia",
      fontSize: descSize,
      fontStyle: "italic",
      fill: 0xaaddff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: panelWidth - 40,
    });
    description.anchor.set(0.5, 0);
    description.x = this.app.screen.width / 2;
    description.y = panelY + 65;
    overlay.addChild(description);

    // Stats
    const statsStartY = panelY + 120;
    const statsSize = Math.min(18, this.app.screen.width / 32);

    // Wave size with color coding
    let sizeColor = 0xffffff;
    if (conditions.size === WaveSize.FLAT) sizeColor = 0x888888;
    else if (conditions.size === WaveSize.SMALL) sizeColor = 0x88ccff;
    else if (conditions.size === WaveSize.MEDIUM) sizeColor = 0x66ff66;
    else if (conditions.size === WaveSize.GOOD) sizeColor = 0xffdd44;
    else if (conditions.size === WaveSize.BIG) sizeColor = 0xff8844;
    else if (conditions.size === WaveSize.HUGE) sizeColor = 0xff4444;

    const sizeText = new PIXI.Text(
      `Wave Size: ${conditions.size.toUpperCase()}`,
      {
        fontFamily: "Georgia",
        fontSize: statsSize,
        fontWeight: "bold",
        fill: sizeColor,
        align: "center",
      }
    );
    sizeText.anchor.set(0.5, 0);
    sizeText.x = this.app.screen.width / 2;
    sizeText.y = statsStartY;
    overlay.addChild(sizeText);

    // Gnarliness bar
    const gnarlText = new PIXI.Text(`Gnarliness: ${conditions.gnarliness}/10`, {
      fontFamily: "Georgia",
      fontSize: statsSize,
      fill: 0xffffff,
      align: "center",
    });
    gnarlText.anchor.set(0.5, 0);
    gnarlText.x = this.app.screen.width / 2;
    gnarlText.y = statsStartY + 40;
    overlay.addChild(gnarlText);

    // Gnarliness bar visual
    const barWidth = 200;
    const barHeight = 20;
    const barX = (this.app.screen.width - barWidth) / 2;
    const barY = statsStartY + 70;

    const barBg = new PIXI.Graphics();
    barBg.beginFill(0x333333);
    barBg.drawRoundedRect(barX, barY, barWidth, barHeight, 10);
    barBg.endFill();
    overlay.addChild(barBg);

    const barFill = new PIXI.Graphics();
    const fillWidth = (conditions.gnarliness / 10) * barWidth;
    let barColor = 0x66ff66;
    if (conditions.gnarliness >= 7) barColor = 0xff8844;
    if (conditions.gnarliness >= 9) barColor = 0xff4444;
    barFill.beginFill(barColor);
    barFill.drawRoundedRect(barX, barY, fillWidth, barHeight, 10);
    barFill.endFill();
    overlay.addChild(barFill);

    // Wind condition with color
    let windColor = 0xffffff;
    if (conditions.wind === WindCondition.OFFSHORE) windColor = 0x66ff66;
    else if (conditions.wind === WindCondition.LIGHT) windColor = 0xaaddff;
    else if (conditions.wind === WindCondition.CROSS) windColor = 0xffdd44;
    else if (conditions.wind === WindCondition.ONSHORE) windColor = 0xff6666;

    const windText = new PIXI.Text(`Wind: ${conditions.wind.toUpperCase()}`, {
      fontFamily: "Georgia",
      fontSize: statsSize,
      fontWeight: "bold",
      fill: windColor,
      align: "center",
    });
    windText.anchor.set(0.5, 0);
    windText.x = this.app.screen.width / 2;
    windText.y = statsStartY + 110;
    overlay.addChild(windText);

    // Impact note
    const impactSize = Math.min(14, this.app.screen.width / 40);
    let impactNote = "";
    if (conditions.size === WaveSize.FLAT) {
      impactNote = "⚠ Flat days give no waves";
    } else if (conditions.size === WaveSize.SMALL) {
      impactNote = "↓ Small waves = fewer sessions";
    } else if (
      conditions.size === WaveSize.HUGE ||
      conditions.gnarliness >= 9
    ) {
      impactNote = "⚠ Scary waves! Lads might refuse";
    } else if (conditions.size === WaveSize.GOOD) {
      impactNote = "✓ Perfect conditions!";
    }

    if (impactNote) {
      const impact = new PIXI.Text(impactNote, {
        fontFamily: "Georgia",
        fontSize: impactSize,
        fontStyle: "italic",
        fill: 0xffdd88,
        align: "center",
      });
      impact.anchor.set(0.5, 0);
      impact.x = this.app.screen.width / 2;
      impact.y = panelY + panelHeight - 40;
      overlay.addChild(impact);
    }

    this.app.stage.addChild(overlay);

    // Add click handler to close overlay
    let timeoutId: NodeJS.Timeout | null = null;
    const closeOverlay = () => {
      if (timeoutId) clearTimeout(timeoutId);
      this.app.stage.removeChild(overlay);
      bg.off('pointerdown', closeOverlay);
      // Trigger callback after overlay is removed
      if (onClose) onClose();
    };

    bg.on('pointerdown', closeOverlay);

    // Auto-remove after delay as fallback
    timeoutId = setTimeout(closeOverlay, 2900);
  }

  private showResourceChanges(effects: StateEffects, message: string): void {
    // Create overlay
    const overlay = new PIXI.Container();

    // Semi-transparent background (not interactive - must wait for auto-close)
    const bg = new PIXI.Graphics();
    bg.beginFill(0x000000, 0.85);
    bg.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    bg.endFill();
    overlay.addChild(bg);

    // Panel
    const panelWidth = Math.min(this.app.screen.width - 40, 400);
    const panelHeight = Math.min(this.app.screen.height - 100, 350);
    const panelX = (this.app.screen.width - panelWidth) / 2;
    const panelY = (this.app.screen.height - panelHeight) / 2;

    const panel = new PIXI.Graphics();
    panel.beginFill(0x2a2a3e);
    panel.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, 15);
    panel.endFill();
    overlay.addChild(panel);

    // Title
    const titleSize = Math.min(22, this.app.screen.width / 25);
    const title = new PIXI.Text("RESOURCE CHANGES", {
      fontFamily: "Arial",
      fontSize: titleSize,
      fontWeight: "bold",
      fill: 0xffaa00,
      align: "center",
    });
    title.anchor.set(0.5, 0);
    title.x = this.app.screen.width / 2;
    title.y = panelY + 15;
    overlay.addChild(title);

    // Message
    const msgSize = Math.min(16, this.app.screen.width / 35);
    const msgText = new PIXI.Text(message, {
      fontFamily: "Arial",
      fontSize: msgSize,
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: panelWidth - 40,
    });
    msgText.anchor.set(0.5, 0);
    msgText.x = this.app.screen.width / 2;
    msgText.y = panelY + 50;
    overlay.addChild(msgText);

    // Changes list
    const changesStartY = panelY + 100;
    const changeSize = Math.min(18, this.app.screen.width / 30);
    let yOffset = 0;

    const changeLabels: { [key: string]: string } = {
      money: "Money",
      groupMorale: "Morale",
      energy: "Energy",
      hangoverLevel: "Hangover",
      surfsSoFar: "Surfs",
      partyCount: "Parties",
      timeOfDay: "Time",
    };

    Object.entries(effects).forEach(([key, value]) => {
      if (value !== undefined && value !== 0) {
        let displayValue = `${value > 0 ? "+" : ""}${value}`;
        if (key === "timeOfDay") {
          displayValue = `+${value}h`;
        }
        const changeText = new PIXI.Text(
          `${changeLabels[key]}: ${displayValue}`,
          {
            fontFamily: "Georgia",
            fontSize: changeSize,
            fontWeight: "bold",
            fill: value > 0 ? 0x00ff00 : 0xff4444,
            align: "center",
          }
        );
        changeText.anchor.set(0.5, 0);
        changeText.x = this.app.screen.width / 2;
        changeText.y = changesStartY + yOffset;
        changeText.alpha = 0;

        // Animate fade in and scale
        const startTime = Date.now();
        const animDuration = 500;
        const delay = yOffset * 100;

        const animate = () => {
          const elapsed = Date.now() - startTime - delay;
          if (elapsed < 0) {
            requestAnimationFrame(animate);
            return;
          }

          const progress = Math.min(elapsed / animDuration, 1);
          changeText.alpha = progress;
          changeText.scale.set(0.8 + progress * 0.2);

          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        animate();

        overlay.addChild(changeText);
        yOffset += 35;
      }
    });

    this.app.stage.addChild(overlay);

    // Remove after delay
    setTimeout(() => {
      this.app.stage.removeChild(overlay);
    }, 2000);
  }

  private refreshCurrentScene(): void {
    const currentScene = this.sceneManager.getCurrentScene();
    if (currentScene) {
      // Recreate the scene with updated state
      let newScene: PIXI.Container;

      switch (currentScene) {
        case GameScene.VILLA:
          newScene = this.createVillaScene();
          break;
        case GameScene.BEACH:
          newScene = this.createBeachScene();
          break;
        case GameScene.BAR:
          newScene = this.createBarScene();
          break;
        default:
          return;
      }

      // Re-register and switch to updated scene
      this.sceneManager.registerScene(currentScene, newScene);
      this.sceneManager.switchTo(currentScene);
    }
  }

  private showGameOver(): void {
    const state = this.gameStateManager.getState();
    const victoryResult = this.gameStateManager.checkVictoryConditions();
    const scoreData = this.gameStateManager.calculateScore();
    const rankTitle = this.gameStateManager.getRankTitle(scoreData.score);

    // Clear all existing scenes and overlays from the stage to prevent layering issues
    this.app.stage.removeChildren();

    // Create full-screen overlay
    const overlay = new PIXI.Container();

    // Black background for padding
    const blackBg = new PIXI.Graphics();
    blackBg.beginFill(0x000000);
    blackBg.drawRect(0, 0, this.app.screen.width, this.app.screen.height);
    blackBg.endFill();
    overlay.addChild(blackBg);

    // Background image - success or failure based on victory conditions
    const bgImage = PIXI.Sprite.from(
      victoryResult.success ? "/assets/success.jpg" : "/assets/failure.jpg"
    );
    this.fitImageToScreen(bgImage, this.app.screen.width, this.app.screen.height);
    overlay.addChild(bgImage);

    const panelWidth = Math.min(this.app.screen.width - 40, 700);
    const panelColor = victoryResult.success ? 0x1a4a2a : 0x4a1a1a;

    // === TOP SECTION ===
    // Top panel for title and message - taller to accommodate victory conditions
    const topPanelHeight = 160;
    const topPanelX = (this.app.screen.width - panelWidth) / 2;
    const topPanelY = 20;

    const topPanel = new PIXI.Graphics();
    topPanel.beginFill(panelColor, 0.7);
    topPanel.drawRoundedRect(topPanelX, topPanelY, panelWidth, topPanelHeight, 15);
    topPanel.endFill();
    overlay.addChild(topPanel);

    // Title
    const titleSize = Math.min(24, this.app.screen.width / 25);
    const title = new PIXI.Text("TRIP COMPLETE!", {
      fontFamily: "Georgia",
      fontSize: titleSize,
      fontWeight: "bold",
      fill: victoryResult.success ? 0x44ff44 : 0xffaa44,
      align: "center",
    });
    title.anchor.set(0.5, 0);
    title.x = this.app.screen.width / 2;
    title.y = topPanelY + 12;
    overlay.addChild(title);

    // Victory/Failure message - smaller font and with scaling if needed
    const msgSize = Math.min(12, this.app.screen.width / 50);
    const victoryMsg = new PIXI.Text(victoryResult.message, {
      fontFamily: "Georgia",
      fontSize: msgSize,
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: panelWidth - 40,
      lineHeight: msgSize * 1.3,
    });
    victoryMsg.anchor.set(0.5, 0);
    victoryMsg.x = this.app.screen.width / 2;
    victoryMsg.y = topPanelY + 45;

    // Scale down message if it overflows the panel
    const maxMessageHeight = topPanelHeight - 55; // Leave space for title and padding
    if (victoryMsg.height > maxMessageHeight) {
      const scaleFactor = maxMessageHeight / victoryMsg.height;
      victoryMsg.scale.set(scaleFactor);
    }

    overlay.addChild(victoryMsg);

    // === CENTER IS LEFT CLEAR FOR IMAGE ===

    // === BOTTOM SECTION ===
    // Bottom panel for stats and score
    const bottomPanelHeight = 180;
    const bottomPanelX = (this.app.screen.width - panelWidth) / 2;
    const bottomPanelY = this.app.screen.height - bottomPanelHeight - 20;

    const bottomPanel = new PIXI.Graphics();
    bottomPanel.beginFill(panelColor, 0.7);
    bottomPanel.drawRoundedRect(bottomPanelX, bottomPanelY, panelWidth, bottomPanelHeight, 15);
    bottomPanel.endFill();
    overlay.addChild(bottomPanel);

    let bottomYPos = bottomPanelY + 15;

    // Stats summary
    const statsSize = Math.min(13, this.app.screen.width / 45);
    const statsContent = `Surfs: ${state.surfsSoFar} | Morale: ${state.groupMorale} | Money: €${state.money} | Parties: ${state.partyCount}`;
    const statsText = new PIXI.Text(statsContent, {
      fontFamily: "Georgia",
      fontSize: statsSize,
      fill: 0xaaccff,
      align: "center",
    });
    statsText.anchor.set(0.5, 0);
    statsText.x = this.app.screen.width / 2;
    statsText.y = bottomYPos;
    overlay.addChild(statsText);
    bottomYPos += statsSize + 12;

    // Score
    const scoreTitleSize = Math.min(18, this.app.screen.width / 30);
    const scoreTitle = new PIXI.Text(`FINAL SCORE: ${scoreData.score}`, {
      fontFamily: "Georgia",
      fontSize: scoreTitleSize,
      fontWeight: "bold",
      fill: 0xffd700,
      align: "center",
    });
    scoreTitle.anchor.set(0.5, 0);
    scoreTitle.x = this.app.screen.width / 2;
    scoreTitle.y = bottomYPos;
    overlay.addChild(scoreTitle);
    bottomYPos += scoreTitleSize + 5;

    // Rank title
    const rankSize = Math.min(16, this.app.screen.width / 35);
    const rankText = new PIXI.Text(rankTitle, {
      fontFamily: "Georgia",
      fontSize: rankSize,
      fontStyle: "italic",
      fill: 0xffaa66,
      align: "center",
    });
    rankText.anchor.set(0.5, 0);
    rankText.x = this.app.screen.width / 2;
    rankText.y = bottomYPos;
    overlay.addChild(rankText);
    bottomYPos += rankSize + 10;

    // Score breakdown - compact and sized to fit available space
    const breakdownSize = Math.min(10, this.app.screen.width / 55);
    // Calculate available space for breakdown (leave 50px for button at bottom)
    const maxBreakdownHeight = bottomPanelHeight - (bottomYPos - bottomPanelY) - 50;

    const breakdownText = new PIXI.Text(scoreData.breakdown.join("\n"), {
      fontFamily: "Courier New",
      fontSize: breakdownSize,
      fill: 0xcccccc,
      align: "left",
      lineHeight: breakdownSize * 1.4,
      wordWrap: true,
      wordWrapWidth: panelWidth - 60,
    });
    breakdownText.anchor.set(0.5, 0);
    breakdownText.x = this.app.screen.width / 2;
    breakdownText.y = bottomYPos;

    // Scale down if text is too tall
    if (breakdownText.height > maxBreakdownHeight) {
      const scaleFactor = maxBreakdownHeight / breakdownText.height;
      breakdownText.scale.set(scaleFactor);
    }

    overlay.addChild(breakdownText);

    // Return to menu button - positioned below the panel to avoid overlap
    const buttonY = bottomPanelY + bottomPanelHeight - 25;
    const menuButton = this.createSmallButton(
      "BACK TO MENU",
      this.app.screen.width / 2,
      buttonY
    );
    menuButton.on("pointerdown", () => {
      this.app.stage.removeChild(overlay);
      this.sceneManager.switchTo(GameScene.MENU);
    });
    overlay.addChild(menuButton);

    this.app.stage.addChild(overlay);
  }
}

// Initialize and start the game
const game = new SurfManagerGame();
game.init().catch((error) => {
  console.error("Failed to initialize game:", error);
});
